import { useDemand, useDigest, useFlags } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { ATLAS_URL, STATUS_URL } from '@/constants/common';
import { NOT_MEASURED, num, when } from '@/lib/format';
import s from './console.module.css';

/**
 * Product: usage by surface, errors, the flags the process runs with (read
 * only), the status page and the ATLAS console. The digest measured usage
 * and errors; the flags feed read the environment; nothing here is set.
 */
export default function ProductPage() {
  const digest = useDigest();
  const flags = useFlags();
  const demand = useDemand();
  const d = digest.data;
  const f = flags.data;
  const on = f ? f.flags.filter((x) => x.on) : [];

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Product</h1>
        <span className={s.pageSub}>
          <a className={s.linkA} href={STATUS_URL} target="_blank" rel="noreferrer">Status page</a>
          {' · '}
          <a className={s.linkA} href={ATLAS_URL} target="_blank" rel="noreferrer">ATLAS marketing console</a>
        </span>
      </div>

      {digest.isError && <div className={s.error}>The digest did not load, so usage and errors are {NOT_MEASURED} right now.</div>}
      {d && (
        <div className={s.strip}>
          <div className={s.stat}><span className={s.statLabel}>Failed checks, 24 h</span><span className={s.statVal}>{num(d.errors.last24h.failedChecks)}</span><span className={s.statSub}>{num(d.errors.last7d.failedChecks)} in 7 days</span></div>
          <div className={s.stat}><span className={s.statLabel}>Aborted checks, 24 h</span><span className={s.statVal}>{num(d.errors.last24h.abortedChecks)}</span><span className={s.statSub}>{num(d.errors.last7d.abortedChecks)} in 7 days</span></div>
          <div className={s.stat}><span className={s.statLabel}>Failed notes, 24 h</span><span className={s.statVal}>{num(d.errors.last24h.failedOutreach)}</span><span className={s.statSub}>{num(d.errors.last24h.sms429)} SMS rate limited</span></div>
          <div className={s.stat}><span className={s.statLabel}>Tickets opened, 24 h</span><span className={s.statVal}>{num(d.errors.last24h.ticketsOpened)}</span><span className={s.statSub}>{num(d.errors.ticketsUnansweredOver24h)} unanswered over a day</span></div>
          <div className={s.stat}><span className={s.statLabel}>Flags on</span><span className={s.statVal}>{f ? num(on.length) : NOT_MEASURED}</span><span className={s.statSub}>{f ? `of ${f.flags.length} · ${f.environment}` : 'reading the process'}</span></div>
        </div>
      )}

      {d && (
        <Section title="Usage by surface" summary={`last ${d.usage.windowDays} days · least used: ${d.usage.leastUsed.length ? d.usage.leastUsed.join(', ') : 'none'}`} count={d.usage.surfaces.length} defaultOpen>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead><tr><th>Surface</th><th>People</th><th>Events</th><th>Minutes</th></tr></thead>
              <tbody>
                {d.usage.surfaces.length === 0 && <tr><td colSpan={4}>Nothing measured in the window.</td></tr>}
                {d.usage.surfaces.map((u) => (
                  <tr key={u.key}>
                    <td>{u.label}</td>
                    <td>{num(u.users)}</td>
                    <td>{num(u.events)}</td>
                    <td>{u.minutes === undefined ? '' : u.minutes === null ? NOT_MEASURED : num(u.minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {d && (
        <Section title="Errors" summary={d.errors.sentry ? `${d.errors.sentry.issues.length} Sentry issues` : `Sentry ${d.errors.sentryReason ?? NOT_MEASURED}`} count={d.errors.last7d.failedChecks + d.errors.last7d.abortedChecks + d.errors.last7d.failedOutreach}>
          <dl className={s.facts}>
            <dt>Failed check reasons, 7 d</dt>
            <dd>{d.errors.last7d.failedCheckReasons.length ? d.errors.last7d.failedCheckReasons.map((r) => `${r.reason} ${r.count}`).join(', ') : 'none recorded'}</dd>
            <dt>Biggest drop, 7 d</dt>
            <dd>{d.funnel.biggestDrop7d ? `${d.funnel.biggestDrop7d.from} to ${d.funnel.biggestDrop7d.to}: ${num(d.funnel.biggestDrop7d.lost)} lost (${num(d.funnel.biggestDrop7d.pct)}%)` : 'none'}</dd>
            <dt>Sentry</dt>
            <dd>
              {d.errors.sentry
                ? (d.errors.sentry.issues.length ? d.errors.sentry.issues.map((i) => `${i.title} (${num(i.count)})`).join('; ') : 'no issues in the window')
                : `${NOT_MEASURED}: ${d.errors.sentryReason ?? 'no reason given'}`}
            </dd>
          </dl>
        </Section>
      )}

      <Section title="Flags on right now" summary={flags.isError ? 'did not load (GET /provider/admin/flags)' : f ? `read from the ${f.environment} process at ${when(f.generatedAt)} · read only, change them in the environment` : 'reading'} count={f ? `${on.length} on` : null} defaultOpen>
        {flags.isError && <div className={s.error}>Could not read the flags. The endpoint is GET /provider/admin/flags on pv-auth.</div>}
        {f && f.flags.map((x) => (
          <div key={x.key} className={s.flagRow}>
            <span className={`${s.pill} ${x.on ? s.pill_on : s.pill_off}`}>{x.on ? 'on' : 'off'}</span>
            <span className={s.flagKey}>{x.key}</span>
            <span className={s.flagWhat}>{x.what}</span>
            <span className={s.sub}>{x.raw === null ? `unset (${x.rule === 'off_when_false' ? 'on by default' : 'off by default'})` : `set to "${x.raw}"`}</span>
          </div>
        ))}
      </Section>

      <Section title="Demand from clinics" summary={demand.data ? `${num(demand.data.totals.customRequests)} requests · ${num(demand.data.totals.followAlongs)} follow-along clips · ${num(demand.data.totals.distinctMovements)} movements` : demand.isError ? 'did not load' : 'loading'} count={demand.data?.items.length ?? null}>
        {demand.data && demand.data.items.length === 0 && <div className={s.empty}>No demand signals yet. Custom-exercise requests and follow-along clips rank here.</div>}
        {demand.data && demand.data.items.length > 0 && (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead><tr><th>#</th><th>Movement</th><th>Requests</th><th>Clips</th><th>Clinics</th><th>Latest</th></tr></thead>
              <tbody>
                {demand.data.items.map((it, i) => (
                  <tr key={it.title}><td>{i + 1}</td><td>{it.title}</td><td>{num(it.customRequests)}</td><td>{num(it.followAlongs)}</td><td>{it.clinics.join(', ')}</td><td>{it.latestAt}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
